import axios from "axios";
import { User } from "../../utils";
import { Company, Document, Employee, Message } from "../../types";
import { hostname, parseError, uniqueId } from "../../utils";

export default class {
  eventListener: Function = () => {};
  getToken: Function = () => {};
  init(listener: Function, getToken: Function) {
    this.eventListener = listener;
    this.getToken = getToken;
  }

  async saveCompanyDefaultChatSettings(companyId: string, settings: any) {
    const token = await this.getToken();
    return axios
      .post(`${hostname}/companies/${companyId}/settings`, settings, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .catch((err) => {
        this.eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  }

  async saveChatSettings(chatId: string, settings: any) {
    const token = await this.getToken();
    return axios
      .post(`${hostname}/chats/${chatId}/settings`, settings, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .catch((err) => {
        this.eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  }

  async syncCompletion(prompt: string, tools: boolean = false) {
    try {
      const token = await this.getToken();
      const response = await fetch(`${hostname}/completions/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tools,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const jsonResponse = await response.json();
      return jsonResponse.content;
    } catch (err) {
      this.eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  }

  async createEmployees(
    company: Company,
    numEmployees: number,
    batchSize: number,
    jobTitle: null | string,
    setMessage: Function
  ) {
    try {
      for (let i = 1; i <= numEmployees; i += batchSize) {
        setMessage(`Generating employee ${i} of ${numEmployees}`);
        await this.createPyEmployee(company, batchSize, jobTitle);
      }
    } catch (err) {
      this.eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  }

  async createPyEmployee(
    company: Company,
    numEmployees: number,
    jobTitle: null | string
  ) {
    try {
      const token = await this.getToken();
      await fetch(`${hostname}/employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          companyId: company.id,
          title: jobTitle,
          numEmployees: numEmployees,
        }),
      });
    } catch (err) {
      this.eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  }

  async sendPyPrompt(
    prompt: string,
    chat: Array<Message>,
    documents: Array<Document>,
    setChat: Function,
    employee: Employee,
    contextSettings: any,
    setPrompt: Function,
    refreshChat: Function,
    company: Company,
    currentUser: User,
    chatId: string,
    useStructuredResponse: boolean = false,
    userPhoto: string = "",
    attachments: Array<{ name: string; type: string; content: string }> = [],
    userDisplayName: string = "You",
    includeAudio: boolean = false,
    abortSignal?: AbortSignal
  ) {
    const token = await this.getToken();

    // User message stores text and attachments separately (blocks are built on render)
    const userMessage: any = {
      id: uniqueId(),
      user: userDisplayName,
      picture: userPhoto || "",
      role: "user",
      text: `${prompt}`,
      ts: new Date().getTime(),
      context: {
        memories: [],
      },
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    var assistantMessage = {
      id: uniqueId(new Date().getTime() + 1),
      user: `${employee.name} (${employee.title})`,
      role: "assistant",
      picture: employee.picture,
      text: "",
      ts: new Date().getTime() + 1,
      context: {
        memories: [],
      },
      blocks: [],
    };
    setChat((chat: any) => {
      return [...chat, userMessage, { ...assistantMessage, loading: true }];
    });
    setPrompt("");

    // Helper to clear the spinner on the assistant placeholder.
    // Used by error paths so the UI never gets stuck.
    const clearLoading = () => {
      setChat((chat: any) =>
        chat.map((x: any) =>
          x.id === assistantMessage.id ? { ...x, loading: false } : x
        )
      );
    };

    // On user abort, drop the assistant placeholder entirely so the chat
    // doesn't leave a half-finished message behind.
    const removeAssistantPlaceholder = () => {
      setChat((chat: any) =>
        chat.filter((x: any) => x.id !== assistantMessage.id)
      );
    };

    let response: Response;
    try {
      response = await fetch(`${hostname}/completions/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: prompt,
          stream: true,
          chatId: chatId,
          employee: employee,
          company: company,
          userMessage: userMessage,
          assistantMessage: assistantMessage,
          contextSettings: contextSettings,
          structuredResponse: useStructuredResponse,
          audio: includeAudio,
        }),
        signal: abortSignal,
      });
    } catch (fetchErr: any) {
      // Aborted before the response was received, or network error.
      if (fetchErr?.name === "AbortError") {
        console.info("Request aborted before response started");
        removeAssistantPlaceholder();
      } else {
        console.error("Fetch error:", fetchErr);
        this.eventListener({
          type: "SET_ERROR",
          error: fetchErr?.message || "Network error",
        });
        clearLoading();
      }
      return;
    }

    if (!response.body) {
      this.eventListener({
        type: "SET_ERROR",
        error: "No response body received from server",
      });
      setChat((chat: any) => {
        return chat.map((x: any) => {
          if (x.id === assistantMessage.id) {
            return {
              ...x,
              text: "Error: No response from server",
              loading: false,
            };
          }
          return x;
        });
      });
      return;
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    // When the user clicks Stop, cancel the reader so the in-flight
    // `reader.read()` promise resolves and the read loop exits cleanly.
    // (Just aborting the fetch isn't enough — pipeThrough(TextDecoderStream)
    // can swallow the abort and leave the read pending forever.)
    let userAborted = false;
    const onAbort = () => {
      userAborted = true;
      reader.cancel().catch(() => {});
    };
    if (abortSignal) {
      if (abortSignal.aborted) {
        onAbort();
      } else {
        abortSignal.addEventListener("abort", onAbort, { once: true });
      }
    }

    var contextJson = "";
    var contextNotDone = false;
    var hasReceivedContent = false;
    var messageBlocks: any[] = [];
    var activeToolCalls = new Map();
    var toolCallOrder: string[] = [];
    var jsonBuffer = ""; // Buffer for incomplete JSON chunks

    try {
      // Set a timeout for the stream - 30 minutes to allow for very long AI responses
      // This timeout only triggers if NO data is received for the entire period
      const timeoutMs = 1800000; // 30 minutes
      let lastChunkTime = Date.now();

      while (true) {
        // User clicked Stop — exit the loop without treating it as an error.
        if (userAborted) {
          break;
        }

        // Check for timeout - only if we haven't received ANY chunks in the timeout period
        // This allows long responses to continue as long as chunks are coming through
        if (Date.now() - lastChunkTime > timeoutMs) {
          throw new Error("Stream timeout: No data received for 30 minutes");
        }

        var { value, done } = await reader.read();

        // Update last chunk time whenever we receive data
        if (value) {
          lastChunkTime = Date.now();
        }

        // reader.cancel() (called from the abort listener) resolves read() to {done: true}.
        // Don't fall through to the "stream ended without content" error path in that case.
        if (userAborted) {
          break;
        }

        if (done) {
          // Process any remaining buffered JSON when stream ends
          if (useStructuredResponse && jsonBuffer.trim()) {
            try {
              const block = JSON.parse(jsonBuffer.trim());
              // Process the final block (simplified - just mark as received)
              if (block.type) {
                messageBlocks.push(block);
                hasReceivedContent = true;
              }
            } catch (e) {
              console.warn("Could not parse remaining buffer on stream end:", jsonBuffer.substring(0, 100));
            }
            jsonBuffer = "";
          }
          break;
        }

        // Handle structured response format
        if (useStructuredResponse && value) {
          try {
            // Append new data to buffer
            jsonBuffer += value;

            // Process complete JSON objects from buffer
            // Each JSON object should be on its own line, separated by newlines
            const lines = jsonBuffer.split('\n');

            // Keep the last line in buffer if it doesn't end with newline (incomplete)
            // If the original value ended with newline, the last element will be empty string
            const lastLine = lines.pop() || "";
            jsonBuffer = lastLine; // Keep incomplete data for next chunk

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (!trimmedLine) continue; // Skip empty lines

              // Check if line looks like a JSON object
              if (!trimmedLine.startsWith('{')) {
                console.warn("Skipping non-JSON line:", trimmedLine.substring(0, 50));
                continue;
              }

              let block: any;
              try {
                block = JSON.parse(trimmedLine);
              } catch (parseError) {
                // If parsing fails, it might be corrupted data - skip it
                console.warn("Skipping malformed JSON:", trimmedLine.substring(0, 100), parseError);
                continue;
              }

              if (block.type === 'text') {
                // Check if the last block is also a text block, merge if so
                const lastBlock = messageBlocks[messageBlocks.length - 1];
                if (lastBlock && lastBlock.type === 'text') {
                  lastBlock.content += block.content;
                } else {
                  messageBlocks.push(block);
                }

                // Update assistant message with merged blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        text: x.text + (block.content || ''),
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'tool') {
                // Track tool call order
                if (!toolCallOrder.includes(block.id)) {
                  toolCallOrder.push(block.id);
                }

                // Update or add tool block
                const existingBlockIndex = messageBlocks.findIndex(
                  (b) => b.type === 'tool' && b.id === block.id
                );

                if (existingBlockIndex >= 0) {
                  messageBlocks[existingBlockIndex] = block;
                } else {
                  messageBlocks.push(block);
                }

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'reasoning_delta') {
                // Streaming reasoning content - merge into existing reasoning block or create new one
                const lastBlock = messageBlocks[messageBlocks.length - 1];
                if (lastBlock && lastBlock.type === 'reasoning') {
                  // Append to existing reasoning block
                  lastBlock.content += block.content;
                } else {
                  // Create new reasoning block for streaming
                  messageBlocks.push({ type: 'reasoning', content: block.content });
                }

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'reasoning') {
                // Final/complete reasoning block
                // If there's already a reasoning block (from streaming), replace its content in place
                // Otherwise add a new one (keeping it at the position it arrives)
                const existingReasoningIdx = messageBlocks.findIndex(b => b.type === 'reasoning');
                if (existingReasoningIdx >= 0) {
                  // Replace content in place to preserve position
                  messageBlocks[existingReasoningIdx].content = block.content;
                } else {
                  messageBlocks.push(block);
                }

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'citations') {
                // Add citations block
                messageBlocks.push(block);

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'file') {
                // Add file block for downloadable documents
                messageBlocks.push(block);

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'install') {
                // Add install block for Composio toolkit installation
                console.log("Received install block:", block);
                messageBlocks.push(block);

                // Update assistant message with blocks
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        blocks: [...messageBlocks],
                        loading: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              } else if (block.type === 'audio') {
                // Audio block - emit event for playback (handled by caller)
                // Don't add to messageBlocks - audio is played, not displayed
                window.dispatchEvent(new CustomEvent('voiceAudioChunk', {
                  detail: {
                    format: block.format,
                    data: block.data,
                    text: block.text
                  }
                }));
                hasReceivedContent = true;
              } else if (block.type === 'error') {
                // Handle error block - show error to user
                this.eventListener({
                  type: "SET_ERROR",
                  error: block.message || "An error occurred",
                });

                // Also update the message to show the error
                setChat((chat: any) => {
                  return chat.map((x: any) => {
                    if (x.id === assistantMessage.id) {
                      return {
                        ...x,
                        text: block.message || "An error occurred",
                        loading: false,
                        error: true,
                      };
                    }
                    return x;
                  });
                });
                hasReceivedContent = true;
              }
            }
            continue;
          } catch (e) {
            // This catch should rarely be hit now that JSON parsing has its own try-catch
            // If we get here, it's likely a different error in the block processing
            console.warn("Error in structured response handling (falling back to legacy):", e);
            // Fall through to legacy parsing
          }
        }

        if (
          (value && contextNotDone) ||
          (value && value.length > 3 && value.slice(0, 3) === "^C^")
        ) {
          try {
            contextNotDone = value.substring(value.length - 3) !== "^C^";
            contextJson += value;
            if (contextNotDone) {
              continue;
            }
            const splits = contextJson.split("^C^");
            const contextStr = splits[1];
            userMessage.context = JSON.parse(contextStr);

            value = splits[2];
            contextNotDone = false;
            contextJson = "";
          } catch (e) {
            console.error("Error parsing json", value, e);
          }
        }
        if (value && value.length > 3 && value.slice(0, 3) === "^E^") {
          const splits = value.split("^E^");
          const errStr = splits[1];
          this.eventListener({
            type: "SET_ERROR",
            error: errStr,
          });
          // Stop loading indicator when error is received
          setChat((chat: any) => {
            return chat.map((x: any) => {
              if (x.id === assistantMessage.id) {
                return {
                  ...x,
                  text: x.text || errStr,
                  loading: false,
                  error: true,
                };
              }
              return x;
            });
          });
          value = splits[2];
        }
        if (value !== undefined && value !== "") {
          hasReceivedContent = true;
          setChat((chat: any) => {
            return chat.map((x: any) => {
              if (x.id === assistantMessage.id) {
                return {
                  ...x,
                  text: x.text + (value || ''),
                  loading: true,
                };
              }
              return x;
            });
          });
        }
      }

      // User clicked Stop. Drop the assistant placeholder so the chat
      // doesn't leave a half-finished message behind. The backend keeps
      // streaming to completion and persists its final state in DB.
      if (userAborted) {
        removeAssistantPlaceholder();
        return;
      }

      // Check if we received any content - only error if assistant message is completely empty
      // This is a safety check, but we shouldn't be too strict since the stream might have
      // sent content that we processed differently
      if (!assistantMessage.text || assistantMessage.text.trim().length === 0) {
        console.warn("Stream ended with no assistant message content");
        // Only throw if we're certain nothing was received
        if (!hasReceivedContent) {
          throw new Error("Stream ended without receiving any response content");
        }
      }

    } catch (streamError: any) {
      // User aborted the request via the stop button - not an error.
      // The backend keeps streaming to completion and saves the message,
      // so we just stop reading and let server-side polling pick up the final state.
      if (streamError?.name === "AbortError") {
        console.info("Stream aborted by user");
        removeAssistantPlaceholder();
        return;
      }
      console.error("Stream error:", streamError);
      this.eventListener({
        type: "SET_ERROR",
        error: streamError.message || "Error reading response stream",
      });

      // Update the assistant message with error state
      setChat((chat: any) => {
        return chat.map((x: any) => {
          if (x.id === assistantMessage.id) {
            return {
              ...x,
              text: x.text || `Error: ${streamError.message || "Failed to receive response"}`,
              loading: false,
            };
          }
          return x;
        });
      });
      return;
    } finally {
      // Always cleanup the reader
      try {
        reader.releaseLock();
      } catch (e) {
        // Ignore errors when releasing lock
      }
      if (abortSignal) {
        abortSignal.removeEventListener("abort", onAbort);
      }
    }

    // Update the final message state and ensure blocks are included
    setChat((chat: any) => {
      return chat.map((x: any) => {
        if (x.id === assistantMessage.id) {
          // Update assistantMessage with final blocks before refresh
          assistantMessage.blocks = x.blocks;
          return {
            ...x,
            loading: false,
          };
        }
        return x;
      });
    });
    refreshChat(userMessage, assistantMessage);
  }

  async getMessages(companyId: string, chatId: string): Promise<any> {
    const token = await this.getToken();
    return axios
      .get(`${hostname}/messages/${companyId}/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        this.eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
        return [];
      });
  }

  async getMessageContext(
    companyId: string,
    chatId: string,
    messageId: string
  ): Promise<any> {
    const token = await this.getToken();
    return axios
      .get(`${hostname}/messages/${companyId}/${chatId}/${messageId}/context`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        this.eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  }

  async streamMessageUpdates(
    companyId: string,
    chatId: string,
    messageId: string,
    onUpdate: (message: any) => void,
    onComplete: () => void,
    onError: (error: string) => void
  ): Promise<() => void> {
    /**
     * Opens a fetch stream to get updates for a specific message.
     * Returns a cleanup function to abort the connection.
     */
    const token = await this.getToken();
    const url = `${hostname}/messages/${companyId}/${chatId}/${messageId}/stream`;

    const abortController = new AbortController();

    const startStream = async () => {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: abortController.signal,
        });

        if (!response.body) {
          onError("No response body received");
          return;
        }

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();

        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            console.log("Message stream completed");
            onComplete();
            break;
          }

          if (value) {
            // Append to buffer and process complete lines
            buffer += value;
            const lines = buffer.split('\n');

            // Keep the last incomplete line in buffer
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (!trimmedLine) continue;

              try {
                // Parse the JSON message update
                const message = JSON.parse(trimmedLine);

                if (message.error) {
                  onError(message.error);
                  return;
                }

                // Update the message in the UI
                onUpdate(message);

                // Close stream if message is no longer loading
                if (!message.loading) {
                  onComplete();
                  return;
                }
              } catch (err) {
                console.error("Error parsing message update:", err, trimmedLine);
                // Continue streaming even if one chunk fails to parse
              }
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("Stream aborted");
        } else {
          console.error("Stream error:", err);
          onError(err.message || "Stream failed");
        }
      }
    };

    // Start the stream
    startStream();

    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }
}
